import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import SnapshotExplorer from '../components/SnapshotExplorer';
import { useAuth } from '../App';
import { formatDate, DEFAULT_TZ, TZ_OPTIONS, useSortable, useIsMobile, useServerHeartbeats } from '../utils';
import { ConfirmModal, DeactivateServerModal } from '../components/Modals';
import { StorageBar } from '../components/client/StorageBar';
import { MonthlyPriceCard } from '../components/MonthlyPriceCard';
import { EditClientModal } from '../components/client/EditClientModal';
import { DelegatedButton } from '../components/client/DelegatedButton';
import { ServersTab } from '../components/client/ServersTab';
import { JobsTab } from '../components/client/JobsTab';
import { RunHistoryTab } from '../components/client/RunHistoryTab';
import { InstallTokenTab } from '../components/client/InstallTokenTab';
import { DeactivatedClientBanner } from '../components/client/DeactivatedClientBanner';


const WASABI_REGIONS = [
  ['us-east-1',      'US East 1 (Ashburn, VA)'],
  ['us-east-2',      'US East 2 (Manassas, VA)'],
  ['us-central-1',   'US Central 1 (Dallas, TX)'],
  ['us-west-1',      'US West 1 (Hillsboro, OR)'],
  ['eu-central-1',   'EU Central 1 (Amsterdam)'],
  ['eu-west-1',      'EU West 1 (London)'],
  ['eu-west-2',      'EU West 2 (París)'],
  ['ap-northeast-1', 'AP Northeast 1 (Tokyo)'],
  ['ap-northeast-2', 'AP Northeast 2 (Osaka)'],
  ['ap-southeast-1', 'AP Southeast 1 (Singapur)'],
  ['ap-southeast-2', 'AP Southeast 2 (Sídney)'],
  ['ca-central-1',   'CA Central 1 (Toronto)'],
];



function Tab({ label, active, onClick }) {
  return (
    <div onClick={onClick} style={{ padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--mono)', borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent', color: active ? 'var(--accent)' : 'var(--muted)', transition: 'all 0.15s' }}>
      {label}
    </div>
  );
}


function AddServerModal({ clientId, onClose, onSaved }) {
  const [form, setForm] = useState({
    hostname: '', ip_address: '', os_type: 'linux', server_type: 'physical',
    timezone: DEFAULT_TZ,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.hostname) { setError('El hostname es obligatorio'); return; }
    setLoading(true);
    try {
      await api.createServer({ ...form, client_id: clientId });
      onSaved();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: 480, clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>AGREGAR SERVIDOR</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          {[['Hostname','hostname','text','srv-prod01'],['IP','ip_address','text','192.168.1.10']].map(([label, key, type, ph]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>{label}</label>
              <input type={type} placeholder={ph} value={form[key]} onChange={e => set(key, e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>OS</label>
              <select value={form.os_type} onChange={e => set('os_type', e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
                <option value="linux">Linux</option>
                <option value="windows">Windows</option>
                <option value="macos">macOS</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Tipo</label>
              <select value={form.server_type} onChange={e => set('server_type', e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
                <option value="physical">Físico</option>
                <option value="vmware">VMware</option>
                <option value="other">Otro</option>
              </select>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Zona Horaria del Servidor</label>
            <select value={form.timezone} onChange={e => set('timezone', e.target.value)}
              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
              {TZ_OPTIONS.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, fontFamily: 'var(--mono)' }}>
              Se usa para mostrar fechas en la hora local del servidor.
            </div>
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
          <button onClick={submit} disabled={loading} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>
            {loading ? 'AGREGANDO...' : 'AGREGAR →'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CovertCopyPanel({ client, onUpdated }) {
  const enabled = client.covert_copy_enabled;

  const [modal, setModal]               = useState(false);
  const [disableModal, setDisableModal] = useState(false);
  const [region, setRegion]             = useState('eu-central-1');
  const [covertBucket, setCovertBucket] = useState('');
  const [step, setStep]                 = useState('select');
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [successMsg, setSuccessMsg]     = useState('');
  const mono = { fontFamily: 'var(--mono)' };

  const openModal = (editMode = false) => {
    setStep('select');
    setInstructions(null);
    setError('');
    if (editMode && enabled) {
      setRegion(client.covert_copy_region || 'eu-central-1');
      setCovertBucket(client.covert_copy_bucket || '');
    } else {
      setRegion('eu-central-1');
      setCovertBucket('');
    }
    setModal(true);
  };

  const fetchInstructions = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.enableCovertCopy(client.id, region, false, null);
      setInstructions(res); setStep('instructions');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    setLoading(true); setError('');
    try {
      await api.enableCovertCopy(client.id, region, true, covertBucket);
      setModal(false);
      setSuccessMsg(enabled ? 'Covert Copy actualizado correctamente' : 'Covert Copy activado correctamente');
      setTimeout(() => setSuccessMsg(''), 3000);
      onUpdated();
    }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true); setError('');
    try {
      await api.disableCovertCopy(client.id);
      setDisableModal(false);
      setSuccessMsg('Covert Copy desactivado en el panel — la réplica sigue existiendo en Wasabi hasta que se dé de baja a mano');
      setTimeout(() => setSuccessMsg(''), 6000);
      onUpdated();
    }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const namePlaceholder = `${client.bucket_name}-covertcopy-AAAAMMDD-HHMMSS  (copialo de Wasabi)`;

  return (
    <>
      {successMsg && (
        <div style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid var(--green)', color: 'var(--green)', padding: '10px 14px', fontSize: 12, ...mono, marginBottom: 12 }}>✓ {successMsg}</div>
      )}
      {error && !modal && (
        <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, ...mono, marginBottom: 12 }}>✕ {error}</div>
      )}

      {/* Banner: operación manual */}
      <div style={{ background: 'rgba(255,200,0,0.06)', border: '1px solid var(--yellow)', padding: '12px 16px', marginBottom: 12, fontSize: 12, lineHeight: 1.6 }}>
        <span style={{ color: 'var(--yellow)', fontWeight: 700, ...mono }}>OPERACIÓN MANUAL EN WASABI</span>
        <span style={{ color: 'var(--muted)', marginLeft: 8 }}>
          Covert Copy se crea y gestiona en la consola de Wasabi (cuenta root de Taxco, con MFA y MUA).
          Este panel registra su configuración para seguimiento y facturación — no la crea automáticamente.
        </span>
      </div>

      <div style={{ background: 'var(--surface)', border: `1px solid ${enabled ? 'var(--green)' : 'var(--border)'}`, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--muted)', ...mono, textTransform: 'uppercase', marginBottom: 8 }}>Covert Copy (Golden Copy)</div>
            {enabled ? (
              <div>
                <div style={{ color: 'var(--green)', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>● Activo</div>
                {client.covert_copy_region && <div style={{ fontSize: 12, color: 'var(--muted)', ...mono }}>Región destino: {WASABI_REGIONS.find(r => r[0] === client.covert_copy_region)?.[1] || client.covert_copy_region}</div>}
                {client.covert_copy_bucket && <div style={{ fontSize: 12, color: 'var(--muted)', ...mono }}>Bucket covert: {client.covert_copy_bucket}</div>}
                {client.covert_copy_last_created && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Configurado: {new Date(client.covert_copy_last_created).toLocaleDateString('es-AR')}</div>}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>No configurado — copia inmutable offline en región alternativa</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {enabled && (
              <button onClick={() => openModal(true)} style={{ background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '6px 14px', fontSize: 11, cursor: 'pointer', ...mono }}>EDITAR</button>
            )}
            {enabled
              ? <button onClick={() => { setError(''); setDisableModal(true); }} disabled={loading} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '6px 14px', fontSize: 11, cursor: 'pointer', ...mono }}>DESACTIVAR</button>
              : <button onClick={() => openModal(false)} style={{ background: 'transparent', border: '1px solid var(--green)', color: 'var(--green)', padding: '6px 14px', fontSize: 11, cursor: 'pointer', ...mono, fontWeight: 700 }}>CONFIGURAR →</button>
            }
          </div>
        </div>
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: 20 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ ...mono, fontSize: 13, letterSpacing: 1 }}>{enabled ? 'EDITAR' : 'CONFIGURAR'} COVERT COPY — {client.name}</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, ...mono, marginBottom: 16 }}>{error}</div>}

              {step === 'select' && (
                <>
                  <div style={{ background: 'rgba(255,200,0,0.06)', border: '1px solid var(--yellow)', padding: '10px 14px', marginBottom: 20, fontSize: 12, ...mono }}>
                    ⚠ PREREQUISITOS: La cuenta root de Taxco en Wasabi debe tener MFA y MUA activos, y el bucket origen debe tener versionado habilitado.
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8, ...mono }}>Región destino del covert copy</label>
                    <select value={region} onChange={e => setRegion(e.target.value)} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', ...mono, fontSize: 13, outline: 'none' }}>
                      {WASABI_REGIONS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>Debe estar en el mismo continente que el bucket origen (<span style={{ ...mono }}>{client.bucket_name}</span>).</div>
                  </div>
                </>
              )}

              {step === 'instructions' && instructions && (
                <>
                  <div style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, ...mono, background: 'var(--bg)', border: '1px solid var(--border)', padding: '10px 14px' }}>
                    <span>Origen: <strong>{instructions.source_bucket}</strong></span>
                    <span style={{ color: 'var(--border)' }}>·</span>
                    <span>Región covert: <strong>{instructions.region_label}</strong></span>
                  </div>

                  {instructions.instructions.map(ins => (
                    <div key={ins.step}>
                      {ins.section && (
                        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--accent)', ...mono, fontWeight: 700, marginTop: ins.step === 1 ? 0 : 24, marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
                          {ins.section}
                        </div>
                      )}
                      <div style={{ marginBottom: 16, paddingLeft: 12, borderLeft: '2px solid var(--border)' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, color: 'var(--text)' }}>Paso {ins.step} — {ins.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>{ins.detail}</div>
                        {ins.cli && (
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '8px 12px', fontSize: 11, ...mono, color: 'var(--green)', wordBreak: 'break-all', marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <span>{ins.cli}</span>
                            <button onClick={() => navigator.clipboard.writeText(ins.cli)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14, flexShrink: 0 }} title="Copiar">⧉</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Formulario de registro — después de los pasos */}
                  <div style={{ marginTop: 24, paddingTop: 20, borderTop: '2px solid var(--border)' }}>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8, ...mono }}>Nombre del bucket covert (generado por Wasabi)</label>
                      <input
                        type="text"
                        value={covertBucket}
                        onChange={e => setCovertBucket(e.target.value.toLowerCase())}
                        placeholder={namePlaceholder}
                        style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', ...mono, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                        El nombre lo genera Wasabi automáticamente al crear la Covert Copy. Copialo de la "Covert Copy Bucket List" de Wasabi y pegalo acá tal cual.
                      </div>
                    </div>
                    <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--muted)' }}>
                      Región elegida: <span style={{ ...mono, color: 'var(--text)' }}>{instructions.region_label} ({instructions.target_region})</span>
                    </div>
                    <button
                      onClick={confirm}
                      disabled={loading || !covertBucket.trim()}
                      style={{ background: 'var(--green)', border: '1px solid var(--green)', color: 'var(--bg)', padding: '9px 20px', fontSize: 12, cursor: (loading || !covertBucket.trim()) ? 'not-allowed' : 'pointer', fontWeight: 700, ...mono, opacity: !covertBucket.trim() ? 0.5 : 1 }}
                    >
                      {loading ? 'GUARDANDO...' : '✓ MARCAR COMO CONFIGURADO'}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button onClick={() => setModal(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 16px', fontSize: 12, cursor: 'pointer', ...mono }}>CANCELAR</button>
              {step === 'select' && (
                <button onClick={fetchInstructions} disabled={loading} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 16px', fontSize: 12, cursor: loading ? 'wait' : 'pointer', fontWeight: 700, ...mono }}>
                  {loading ? 'GENERANDO...' : 'VER INSTRUCCIONES →'}
                </button>
              )}
              {step === 'instructions' && (
                <button onClick={() => setStep('select')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '7px 16px', fontSize: 12, cursor: 'pointer', ...mono }}>← VOLVER</button>
              )}
            </div>
          </div>
        </div>
      )}

      {disableModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: 20 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: '95vw', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ ...mono, fontSize: 13, letterSpacing: 1 }}>DESACTIVAR COVERT COPY — {client.name}</span>
              <button onClick={() => setDisableModal(false)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 24 }}>
              {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, ...mono, marginBottom: 16 }}>{error}</div>}
              <div style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 14 }}>
                Esto sólo borra la configuración del panel y deja de facturarla en la consola de OpenBackup.
              </div>
              <div style={{ background: 'rgba(255,200,0,0.08)', border: '1px solid var(--yellow)', padding: '12px 16px', fontSize: 12, ...mono, lineHeight: 1.7, color: 'var(--yellow)' }}>
                ⚠ IMPORTANTE: la réplica sigue existiendo en Wasabi y sigue facturando ahí. El covert copy no tiene API:
                darlo de baja es un trámite manual en la consola de Wasabi con la cuenta root (MFA + MUA) y aprobación
                cruzada de varias personas. Queda registrada una lápida para la auditoría, pero el borrado real no es
                automático.
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setDisableModal(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 16px', fontSize: 12, cursor: 'pointer', ...mono }}>CANCELAR</button>
              <button onClick={disable} disabled={loading} style={{ background: 'var(--red)', border: 'none', color: '#fff', padding: '7px 16px', fontSize: 12, cursor: loading ? 'wait' : 'pointer', fontWeight: 700, ...mono }}>
                {loading ? 'GUARDANDO...' : 'ENTIENDO, DESACTIVAR'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CrossServerRestorePanel({ client, onUpdated }) {
  const enabled = client.allow_cross_server_restore;
  const [modal, setModal]       = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const mono = { fontFamily: 'var(--mono)' };

  const toggle = async () => {
    setLoading(true); setError('');
    try {
      await api.updateCrossServerRestore(client.id, !enabled);
      setModal(false);
      setSuccessMsg(enabled ? 'Restore entre servidores desactivado' : 'Restore entre servidores activado');
      setTimeout(() => setSuccessMsg(''), 3000);
      onUpdated();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <>
      {successMsg && (
        <div style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid var(--green)', color: 'var(--green)', padding: '10px 14px', fontSize: 12, ...mono, marginBottom: 12 }}>✓ {successMsg}</div>
      )}
      <div style={{ background: 'var(--surface)', border: `1px solid ${enabled ? 'var(--green)' : 'var(--border)'}`, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--muted)', ...mono, textTransform: 'uppercase', marginBottom: 8 }}>Restaurar en otro servidor</div>
            <div style={{ color: enabled ? 'var(--green)' : 'var(--muted)', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
              {enabled ? '● Activado' : 'Desactivado'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', maxWidth: 520, lineHeight: 1.6 }}>
              Permite tomar un backup hecho en un servidor y restaurarlo en OTRO servidor de este mismo cliente —
              útil para migrar a una máquina nueva, verificar un backup sin tocar producción, y sobre todo para
              recuperar los datos cuando la máquina original ya no existe.
            </div>
          </div>
          <button onClick={() => setModal(true)} style={{ background: 'transparent', border: `1px solid ${enabled ? 'var(--red)' : 'var(--green)'}`, color: enabled ? 'var(--red)' : 'var(--green)', padding: '6px 14px', fontSize: 11, cursor: 'pointer', ...mono, fontWeight: 700, flexShrink: 0 }}>
            {enabled ? 'DESACTIVAR' : 'ACTIVAR'}
          </button>
        </div>
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: 20 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: '95vw', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ ...mono, fontSize: 13, letterSpacing: 1 }}>{enabled ? 'DESACTIVAR' : 'ACTIVAR'} RESTORE ENTRE SERVIDORES</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 24 }}>
              {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, ...mono, marginBottom: 16 }}>{error}</div>}
              {enabled ? (
                <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                  A partir de que la desactives, sólo se van a poder restaurar backups en el mismo servidor que los generó, como antes. Los restores cruzados que ya se hicieron no se ven afectados.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 14 }}>
                    Esta opción permite restaurar un backup hecho en un servidor de este cliente en OTRO servidor
                    del mismo cliente. Sirve para migrar a una máquina nueva, verificar un backup en un servidor de
                    prueba sin tocar producción, y — el caso más importante — recuperar los datos cuando la máquina
                    original ya no existe.
                  </div>
                  <div style={{ background: 'rgba(255,200,0,0.08)', border: '1px solid var(--yellow)', padding: '12px 16px', fontSize: 12, ...mono, lineHeight: 1.7, color: 'var(--yellow)' }}>
                    ⚠ IMPORTANTE: al activarla, cualquier servidor de este cliente pasa a poder leer los backups de
                    TODOS los demás servidores del mismo cliente, no sólo los suyos. Si uno de los servidores se ve
                    comprometido, ese acceso también queda comprometido. Son los datos y las máquinas del cliente —
                    la decisión de asumir ese riesgo es suya.
                  </div>
                </>
              )}
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setModal(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 16px', fontSize: 12, cursor: 'pointer', ...mono }}>CANCELAR</button>
              <button onClick={toggle} disabled={loading} style={{ background: enabled ? 'var(--red)' : 'var(--green)', border: 'none', color: enabled ? '#fff' : 'var(--bg)', padding: '7px 16px', fontSize: 12, cursor: loading ? 'wait' : 'pointer', fontWeight: 700, ...mono }}>
                {loading ? 'GUARDANDO...' : (enabled ? 'DESACTIVAR' : 'ENTIENDO, ACTIVAR')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


export default function ClientDetail() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { id }   = useParams();
  const { isAdmin, isOperator, isClient, isReseller, me, resellerPerms, perms } = useAuth();
  const [client, setClient]     = useState(null);
  // #210 etapa 2: en la propia empresa, lo sensible (clave, kit, delegaciones, restore entre servidores)
  // es de quien administra la cuenta; explorar y restaurar, de cualquier usuario de la empresa
  const isOwnCompany = isReseller && !!client?.is_reseller && client?.id === me?.client_id;
  // #210 etapa 2 parte 2: una empresa reseller sin plan no instala agentes (el backend responde 409)
  const noInfraText = (client?.is_reseller && !client?.internal && !client?.plan)
    ? (isReseller ? 'Sin infraestructura propia: pedile a OpenBackup que te asigne un plan.'
                  : 'Esta empresa no tiene plan: asignale uno para instalar agentes.')
    : null;
  const canActAsOwner = !isOwnCompany || !!resellerPerms?.can_manage_account;
  // #210 etapa 2: quién escribe jobs. El usuario client, según el ajuste de su cliente.
  const canManageJobs = isAdmin || (isOperator && !!perms?.can_manage_jobs) || (isReseller && !!resellerPerms?.can_manage_jobs)
    || (isClient && client?.client_can_manage_jobs !== false);
  const [servers, setServers]   = useState([]);
  const heartbeatFeed = useServerHeartbeats(id);
  const [runs, setRuns]         = useState([]);
  const [explorer, setExplorer]  = useState(null);
  const [plans, setPlans]       = useState([]);
  const [token, setToken]       = useState(null);
  const [tab, setTab]           = useState('servers');
  const [editModal, setEditModal]           = useState(false);
  const [addServer, setAddServer]           = useState(false);
  const [copied, setCopied]                 = useState(false);
  const [copiedWin, setCopiedWin]           = useState(false);
  const [copiedAlpine, setCopiedAlpine]     = useState(false);
  const [loading, setLoading]               = useState(true);
  const [repoKeyLoadingId, setRepoKeyLoadingId] = useState(null);
  const [kitLoading, setKitLoading]         = useState(false);
  const [reprovisioning, setReprovisioning] = useState(false);
  const [confirmDeleteServer, setConfirmDeleteServer] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [transferModal, setTransferModal]   = useState(null);
  const [allClients, setAllClients]         = useState([]);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferring, setTransferring]     = useState(false);
  const [repoKeyModal, setRepoKeyModal]     = useState(null);
  const [delegations, setDelegations]           = useState(null);
  const [overrideToken, setOverrideToken]       = useState(null);
  const [overrideActive, setOverrideActive]     = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason]     = useState('');
  const [overrideLoading, setOverrideLoading]   = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [toast, setToast] = useState({ msg: '', type: '' });
  const showToast = (msg, type = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 3000); };

  const serverTzMap = Object.fromEntries(servers.map(s => [s.id, s.timezone || DEFAULT_TZ]));

  const loadServers = async (inactive) => {
    try { setServers(await api.getServers(id, inactive)); } catch (e) { console.error(e); }
  };

  const load = async () => {
    try {
      const [c, s, p] = await Promise.all([api.getClient(id), api.getServers(id, showInactive), api.getPlans()]);
      setClient(c); setServers(s); setPlans(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadRuns = async () => {
    try { setRuns(await api.getRecentRuns(50, id)); } catch (e) { console.error(e); }
  };

  const generateToken = async () => {
    setTokenLoading(true);
    try { setToken(await api.getToken(id)); } catch (e) { console.error(e); } finally { setTokenLoading(false); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (client) loadServers(showInactive); }, [showInactive]);
  useEffect(() => { if (tab === 'history') loadRuns(); }, [tab]);
  // Auto-refresh cada 5s mientras haya runs en curso y la pestaña activa sea
  // Historial — mismo patrón que Jobs.js. Sin runs corriendo, cero llamadas.
  useEffect(() => {
    if (tab !== 'history') return;
    const t = setInterval(() => {
      setRuns(prev => {
        const hasRunning = prev.some(r => r.status === 'running');
        if (hasRunning) loadRuns();
        return prev;
      });
    }, 5000);
    return () => clearInterval(t);
  }, [tab]); // eslint-disable-line
  useEffect(() => { if (isAdmin) api.getClients().then(setAllClients).catch(() => {}); }, [isAdmin]);

  useEffect(() => {
    if (!isClient && id) {
      if (client?.internal) {
        setDelegations({ can_explore: true, can_restore: true, can_view_key: true, can_recovery_kit: true });
        return;
      }
      api.getDelegationCheck(id)
        .then(setDelegations)
        .catch(() => setDelegations({
          can_explore: false, can_restore: false,
          can_view_key: false, can_recovery_kit: false,
        }));
    }
  }, [id, isClient, client?.internal]);

  useEffect(() => {
    return () => {
      setOverrideActive(false);
      setOverrideToken(null);
    };
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(token.install_cmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyWin = () => {
    navigator.clipboard.writeText(`iwr ${token.download_url_windows} -UseBasicParsing | iex`);
    setCopiedWin(true);
    setTimeout(() => setCopiedWin(false), 2000);
  };
  const copyAlpine = () => {
    const alpineCmd = token?.install_cmd
      ? `apk add --no-cache bash curl ca-certificates && ${token.install_cmd.replace('| sudo bash', '| bash')}`
      : '';
    navigator.clipboard.writeText(alpineCmd);
    setCopiedAlpine(true);
    setTimeout(() => setCopiedAlpine(false), 2000);
  };

  const handleViewRepoKey = async (server) => {
    setRepoKeyLoadingId(server.id);
    try {
      const data = await api.getRecoveryKey(id, server.id);
      setRepoKeyModal(data);
    } catch (e) {
      alert(e.message);
    } finally {
      setRepoKeyLoadingId(null);
    }
  };

  const handleReprovision = async () => {
    setReprovisioning(true);
    try {
      await api.reprovisionClient(id);
      alert('Provisionamiento iniciado. Actualizá en 30 segundos.');
    } catch (e) {
      alert(e.message);
    } finally {
      setReprovisioning(false);
    }
  };

  const handleRequestSubdelegation = async () => {
    if (!delegations?.delegation_id) return;
    try {
      await api.requestSubdelegation(delegations.delegation_id, null);
      api.getDelegationCheck(id).then(setDelegations).catch(() => {});
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const { sorted: sortedRuns, sortKey: runSortKey, sortDir: runSortDir, toggle: runToggle } = useSortable(runs, 'started_at', 'desc');

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;
  if (!client) return <div style={{ padding: 40, fontFamily: 'var(--mono)', color: 'var(--red)' }}>Cliente no encontrado</div>;

  const serverId = servers.length > 0 ? servers[0]?.id : null;

  return (
    <div>
      {toast.msg && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toast.type === 'ok' ? 'rgba(0,229,160,0.12)' : 'rgba(255,77,109,0.12)',
          border: `1px solid ${toast.type === 'ok' ? 'var(--green)' : 'var(--red)'}`,
          color: toast.type === 'ok' ? 'var(--green)' : 'var(--red)',
          padding: '10px 18px', fontSize: 12, fontFamily: 'var(--mono)',
        }}>
          {toast.type === 'ok' ? '✓' : '✕'} {toast.msg}
        </div>
      )}
      {editModal && <EditClientModal client={client} plans={plans} onClose={() => setEditModal(false)} onSaved={load} />}
      {addServer && <AddServerModal clientId={id} onClose={() => setAddServer(false)} onSaved={() => { load(); showToast('Servidor agregado correctamente'); }} />}

      {/* OVERRIDE MODAL */}
      {showOverrideModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--yellow)', width: 480, padding: 24 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--yellow)', marginBottom: 12 }}>Override de emergencia</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              El cliente no ha otorgado delegación. El override permite acceso temporal (1 hora) y queda registrado en el audit log.
            </div>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Motivo (mínimo 10 caracteres)</label>
            <textarea
              value={overrideReason}
              onChange={e => setOverrideReason(e.target.value)}
              placeholder="Descripción del motivo de emergencia..."
              style={{ width: '100%', minHeight: 80, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setShowOverrideModal(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
              <button
                disabled={overrideLoading || overrideReason.length < 10}
                onClick={async () => {
                  setOverrideLoading(true);
                  try {
                    const result = await api.requestOverride({
                      client_id: id,
                      reason: overrideReason,
                      operations: ['explore', 'restore', 'view_key', 'recovery_kit'],
                    });
                    setOverrideToken(result.override_token);
                    setOverrideActive(true);
                    setShowOverrideModal(false);
                    setOverrideReason('');
                  } catch (e) {
                    alert('Error: ' + e.message);
                  } finally {
                    setOverrideLoading(false);
                  }
                }}
                style={{ background: 'var(--yellow)', border: '1px solid var(--yellow)', color: 'var(--bg)', padding: '7px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700, opacity: overrideReason.length < 10 ? 0.5 : 1 }}
              >
                {overrideLoading ? 'PROCESANDO...' : 'ACTIVAR OVERRIDE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteServer && (
        <DeactivateServerModal
          hostname={confirmDeleteServer.hostname}
          onConfirm={async () => {
            const { id: serverId, hostname: name } = confirmDeleteServer;
            setConfirmDeleteServer(null);
            try {
              await api.deleteServer(serverId);
              showToast(`Servidor ${name} dado de baja`);
              load();
            } catch (e) {
              showToast(e.message || 'Error al dar de baja el servidor', 'error');
            }
          }}
          onCancel={() => setConfirmDeleteServer(null)}
        />
      )}

      {/* REPO KEY MODAL */}
      {repoKeyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: 560, clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>🔑 CLAVE DE RECUPERACIÓN</span>
              <button onClick={() => setRepoKeyModal(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ background: 'rgba(255,200,0,0.08)', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '10px 14px', fontSize: 11, fontFamily: 'var(--mono)', marginBottom: 20 }}>
                ⚠ Esta información permite acceso completo al repositorio. No compartir. Acceso registrado.
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 4 }}>Servidor</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 14, marginBottom: 16 }}>{repoKeyModal.client_name} / {repoKeyModal.server_hostname}</div>
              {[
                { label: 'RESTIC_REPOSITORY', value: repoKeyModal.restic_repository },
                { label: 'RESTIC_PASSWORD (repo_key)', value: repoKeyModal.repo_key },
                { label: 'AWS_ACCESS_KEY_ID', value: repoKeyModal.wasabi_access_key },
                { label: 'AWS_SECRET_ACCESS_KEY', value: repoKeyModal.wasabi_secret_key },
              ].map(({ label, value }) => (
                <div key={label} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--mono)' }}>{label}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input readOnly value={value || '—'} style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--accent)', padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none' }} />
                    <button onClick={() => navigator.clipboard.writeText(value || '')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '8px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', flexShrink: 0 }}>⧉</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setRepoKeyModal(null)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CERRAR</button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <button onClick={() => navigate('/clients')} style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 12 }}>← Clientes</button>
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 22 }}>{client.name}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            {client.city || '—'} · plan {client.plan?.name || '—'} · desde {formatDate(client.created_at)}
          </div>
          {isAdmin && client.bucket_name && (
            <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--border)' }}>bucket:</span>
              <span>{client.bucket_name}</span>
              <span style={{ color: 'var(--border)', marginLeft: 8 }}>iam:</span>
              <span>{client.bucket_name}</span>
              <button onClick={() => navigator.clipboard.writeText(client.bucket_name)} style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 11, padding: '0 4px' }} title="Copiar">⧉</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isAdmin && overrideActive && !client.internal && (
            <span style={{ fontSize: 11, color: '#ff8800', fontFamily: 'var(--mono)', border: '1px solid #ff8800', padding: '4px 10px' }}>
              OVERRIDE ACTIVO
              <button onClick={() => { setOverrideActive(false); setOverrideToken(null); }} style={{ background: 'none', border: 'none', color: '#ff8800', cursor: 'pointer', marginLeft: 8, fontSize: 11 }}>✕</button>
            </span>
          )}
          {isAdmin && !overrideActive && !client.internal && (
            <button
              onClick={() => setShowOverrideModal(true)}
              style={{ background: 'transparent', border: '1px solid rgba(255,136,0,0.4)', color: 'rgba(255,136,0,0.7)', padding: '7px 14px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}
              title="Acceso de emergencia cuando el cliente no ha delegado"
            >
              Override
            </button>
          )}
          {(isAdmin || isOperator || isReseller || isClient) && canActAsOwner && (
            <DelegatedButton
              operation="can_recovery_kit"
              delegations={delegations}
              isClient={isClient}
              overrideActive={overrideActive}
              disabled={kitLoading}
              onClick={async () => {
                setKitLoading(true);
                try {
                  const kit = await api.getRecoveryKit(id);
                  const blob = new Blob([JSON.stringify(kit, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `recovery-kit-${client.name.replace(/ /g, '_')}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { alert(e.message); }
                finally { setKitLoading(false); }
              }}
              style={{ background: 'transparent', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '7px 16px', fontSize: 11, fontFamily: 'var(--mono)', marginRight: 8, opacity: kitLoading ? 0.5 : 1 }}
            >
              {kitLoading ? '...' : 'KIT RECUPERACION'}
            </DelegatedButton>
          )}
          {(isAdmin || isOperator || (isReseller && resellerPerms?.can_edit_clients)) && (
            <button onClick={() => setEditModal(true)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>EDITAR</button>
          )}
        </div>
      </div>

      {isAdmin && <DeactivatedClientBanner client={client} />}

      {isAdmin && !client.bucket_name && (
        <div style={{
          background: 'rgba(255,100,0,0.1)',
          border: '1px solid var(--yellow)',
          color: 'var(--yellow)',
          padding: '10px 16px',
          marginBottom: 16,
          fontSize: 13,
          fontFamily: 'var(--mono)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>⚠ Almacenamiento no configurado — el instalador fallará hasta que se provisione el bucket.</span>
          <button
            onClick={handleReprovision}
            disabled={reprovisioning}
            style={{
              background: 'var(--yellow)', color: 'var(--bg)',
              border: 'none', padding: '6px 14px',
              fontFamily: 'var(--mono)', fontSize: 11,
              fontWeight: 700, cursor: reprovisioning ? 'not-allowed' : 'pointer',
              opacity: reprovisioning ? 0.5 : 1,
            }}
          >
            {reprovisioning ? 'PROVISIONANDO...' : 'PROVISIONAR AHORA'}
          </button>
        </div>
      )}

      {/* STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 16, marginBottom: 16 }}>
        {[
          { label: 'Servidores', value: servers.length,                              color: 'var(--accent)' },
          { label: 'Plan',       value: client.plan?.name || '—',                    color: 'var(--green)' },
          { label: 'Retención',  value: client.plan?.retention_days ? client.plan.retention_days+' días' : '—',   color: 'var(--muted)' },
        ].map((s,i) => (
          <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 16, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.color }} />
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* STORAGE BAR */}
      {!client.internal && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 20, marginBottom: 24 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 12 }}>Storage</div>
          <StorageBar
            usedGb={client.storage_used_gb ?? 0}
            quotaGb={client.storage_quota_gb}
            pct={client.storage_pct}
          />
        </div>
      )}

      {/* PRECIO MENSUAL */}
      {/* #210 etapa 2: el reseller ve precios sólo con can_view_billing */}
      {(!isReseller || resellerPerms?.can_view_billing) && (
        <MonthlyPriceCard client={client} isAdmin={isAdmin} isReseller={isReseller} />
      )}

      {(isAdmin || (isOperator && perms.can_manage_covert_copy)) && (
        <CovertCopyPanel client={client} onUpdated={load} />
      )}

      {/* #210 etapa 2: en la propia empresa, el interruptor es de quien administra la cuenta */}
      {(isAdmin || isOperator || isClient || (isOwnCompany && resellerPerms?.can_manage_account)) && (
        <CrossServerRestorePanel client={client} onUpdated={load} />
      )}

      {/* TABS */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {[['servers','Servidores'],['jobs','Jobs'],['history','Historial'],['token','Token de Instalación']].map(([tid, label]) => (
          <Tab key={tid} label={label} active={tab === tid} onClick={() => setTab(tid)} />
        ))}
      </div>

      {/* RESELLER DELEGATION BANNER */}
      {isReseller && delegations && !delegations.is_own_client && (delegations.can_explore || delegations.can_restore || delegations.can_view_key || delegations.can_recovery_kit) && (
        <div style={{ background: 'rgba(0,150,255,0.08)', border: '1px solid rgba(0,150,255,0.3)', padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
          El cliente te ha delegado:{' '}
          {[
            delegations.can_explore      && 'Explorar',
            delegations.can_restore      && 'Restaurar',
            delegations.can_view_key     && 'Ver clave',
            delegations.can_recovery_kit && 'Kit de recuperación',
          ].filter(Boolean).join(', ')}
          {delegations.subdelegation_status === 'none' && (
            <button onClick={handleRequestSubdelegation} style={{ marginLeft: 16, fontSize: 12, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(0,150,255,0.5)', color: 'rgba(0,150,255,0.9)', padding: '3px 10px', fontFamily: 'var(--mono)' }}>
              Solicitar sub-delegación al admin
            </button>
          )}
          {delegations.subdelegation_status === 'pending_client_approval' && (
            <span style={{ marginLeft: 16, color: 'var(--muted)', fontSize: 12 }}>Solicitud enviada al cliente. Esperando aprobación.</span>
          )}
          {delegations.subdelegation_status === 'active' && (
            <span style={{ marginLeft: 16, color: '#00cc66', fontSize: 12 }}>Sub-delegación al admin activa.</span>
          )}
          {delegations.subdelegation_status === 'rejected' && (
            <span style={{ marginLeft: 16, color: '#ff4444', fontSize: 12 }}>El cliente rechazó la sub-delegación.</span>
          )}
        </div>
      )}

      {/* SERVERS */}
      {tab === 'servers' && (
        <ServersTab
          servers={servers}
          heartbeatFeed={heartbeatFeed}
          delegations={delegations}
          isClient={isClient}
          isAdmin={isAdmin}
          isOperator={isOperator}
          isReseller={isReseller}
          isOwnCompany={isOwnCompany}
          resellerPerms={resellerPerms}
          operatorPerms={perms}
          overrideActive={overrideActive}
          client={client}
          id={id}
          handleViewRepoKey={handleViewRepoKey}
          repoKeyLoadingId={repoKeyLoadingId}
          setTransferModal={setTransferModal}
          setTransferTarget={setTransferTarget}
          setConfirmDeleteServer={setConfirmDeleteServer}
          setAddServer={setAddServer}
          noInfraText={noInfraText}
          setTab={setTab}
          onGenerateToken={generateToken}
          onReload={load}
          showInactive={showInactive}
          setShowInactive={setShowInactive}
        />
      )}

      {/* JOBS */}
      {tab === 'jobs' && (
        <JobsTab clientId={client.id} servers={servers} canWrite={canManageJobs} hideConfig={!isClient && !canManageJobs} />
      )}

      {/* HISTORY */}
      {tab === 'history' && (
        <RunHistoryTab
          sortedRuns={sortedRuns}
          serverTzMap={serverTzMap}
          isMobile={isMobile}
          delegations={delegations}
          isClient={isClient}
          overrideActive={overrideActive}
          setExplorer={setExplorer}
          runSortKey={runSortKey}
          runSortDir={runSortDir}
          runToggle={runToggle}
        />
      )}

      {/* RETENTION */}
      {/* TOKEN */}
      {tab === 'token' && (isAdmin || isOperator || isClient || (isReseller && resellerPerms?.can_generate_tokens)) && (
        <InstallTokenTab
          token={token}
          copied={copied}
          copiedWin={copiedWin}
          copiedAlpine={copiedAlpine}
          onCopy={copy}
          onCopyWin={copyWin}
          onCopyAlpine={copyAlpine}
          onRegenerate={generateToken}
          noInfraText={noInfraText}
          loading={tokenLoading}
        />
      )}

      {explorer && <SnapshotExplorer
        run={explorer}
        serverId={explorer?.server_id || serverId}
        server={servers.find(s => s.id === (explorer?.server_id || serverId))}
        onClose={() => setExplorer(null)}
        defaultTarget={explorer.paths?.[0] || ''}
        clientServers={servers}
        heartbeatFeed={heartbeatFeed}
        allowCrossServerRestore={!!client?.allow_cross_server_restore}
      />}

      {transferModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: 460, clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>TRANSFERIR SERVIDOR</span>
              <button onClick={() => setTransferModal(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ marginBottom: 16, fontSize: 13 }}>
                <span style={{ color: 'var(--muted)' }}>Servidor: </span>
                <strong style={{ fontFamily: 'var(--mono)' }}>{transferModal.hostname}</strong>
              </div>
              <div style={{ marginBottom: 20, padding: 12, background: 'rgba(255,200,0,0.08)', border: '1px solid var(--yellow)', fontSize: 12, color: 'var(--yellow)' }}>
                ⚠ El servidor y todos sus jobs serán reasignados al cliente seleccionado. El repositorio de backup no cambia.
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Cliente destino</label>
                <select
                  value={transferTarget}
                  onChange={e => setTransferTarget(e.target.value)}
                  style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}
                >
                  <option value="">— Seleccioná un cliente —</option>
                  {allClients.filter(c => c.id !== client?.id && !c.internal).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setTransferModal(null)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
              <button
                disabled={!transferTarget || transferring}
                onClick={async () => {
                  setTransferring(true);
                  try {
                    await api.transferServer(transferModal.id, transferTarget);
                    setTransferModal(null);
                    showToast('Servidor transferido correctamente');
                    load();
                  } catch (e) { showToast(e.message || 'Error al transferir el servidor', 'error'); }
                  finally { setTransferring(false); }
                }}
                style={{ background: transferTarget ? 'var(--accent)' : 'var(--border)', border: 'none', color: transferTarget ? 'var(--bg)' : 'var(--muted)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: transferTarget ? 'pointer' : 'not-allowed', fontWeight: 700 }}
              >
                {transferring ? 'TRANSFIRIENDO...' : 'CONFIRMAR →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
